/**
 * The desktop bridge as the web app sees it (ADR 0033). In the Desktop client
 * its preload puts `window.onlymindDesktop` on the page; in a browser there is
 * nothing there and every call below does nothing. This is the only file that
 * touches that object — features call these functions, never the global.
 *
 * Mirrors desktop/src/shared/bridge.ts (protocol v1). The web app ships ahead
 * of installed desktop apps, so it must keep working with older bridges:
 * check for a function before using anything added after v1.
 */
export interface OnlymindDesktopBridge {
  readonly bridgeVersion: number
  readonly appVersion: string
  readonly platform: 'win32' | 'darwin' | 'linux'
  setBadgeCount(count: number): void
  focus(): void
}

declare global {
  interface Window {
    onlymindDesktop?: unknown
  }
}

export function desktopBridge(): OnlymindDesktopBridge | null {
  if (typeof window === 'undefined') return null
  const b = window.onlymindDesktop as Partial<OnlymindDesktopBridge> | null | undefined
  if (!b || typeof b !== 'object') return null
  if (typeof b.bridgeVersion !== 'number' || b.bridgeVersion < 1) return null
  if (typeof b.setBadgeCount !== 'function' || typeof b.focus !== 'function') return null
  return b as OnlymindDesktopBridge
}

export function isDesktop(): boolean {
  return desktopBridge() !== null
}

/** The Workspace badge on the app icon; 0 clears it. */
export function setDesktopBadge(count: number) {
  try {
    desktopBridge()?.setBadgeCount(Number.isFinite(count) && count > 0 ? Math.floor(count) : 0)
  } catch {
    /* the shell went away */
  }
}

export function focusDesktopWindow() {
  try {
    desktopBridge()?.focus()
  } catch {
    /* the shell went away */
  }
}

// A notification click happens outside React; DesktopIntegration hands us the
// router's navigate so it can open the chat without reloading the app.
let navigateTo: ((to: string) => void) | null = null

export function setDesktopNavigator(fn: (to: string) => void): () => void {
  navigateTo = fn
  return () => {
    if (navigateTo === fn) navigateTo = null
  }
}

/** A desktop notification was clicked: bring the window back and open what it was about. */
export function openFromNotification(href?: string) {
  focusDesktopWindow()
  if (href && href.startsWith('/') && !href.startsWith('//')) navigateTo?.(href)
}
